import type { PMTranslations } from "../../types/pm";

export const pm: PMTranslations = {
  sidebar: {
    sectionLabel: "Project Management",
    issues: "Issues",
    myIssues: "My Issues",
    notifications: "Notifications",
  },
  myIssues: {
    title: "My Issues",
    subtitle: "Issues assigned to you, created by you, or mentioning you",
    tabs: { assigned: "Assigned", created: "Created", mentions: "Mentions" },
    empty: {
      assigned: {
        title: "No issues assigned to you",
        subtitle: "Issues where you're an assignee will show up here.",
      },
      created: {
        title: "You haven't created any issues",
        subtitle: "Issues you create will show up here.",
      },
      mentions: {
        title: "No mentions yet",
        subtitle: "Issues where you're @mentioned in a comment will show up here.",
      },
      overdue: {
        title: "No overdue issues",
        subtitle: "Issues assigned to you that are past their due date will show up here.",
      },
      filteredHint: "Clear your filters or project selection to see them.",
    },
  },
  issues: {
    title: "Issues",
    subtitle: "Track and manage project issues",
    newIssue: "New Issue",
    linkingLabel: "Linking:",
    deepLinkFailed:
      "That issue couldn't be opened. It may have been deleted, or you may not have access to it.",
    deepLinkUnavailable:
      "That issue couldn't be loaded just now. Open the link you followed again, or find the issue in the list.",
    saveWithSection: "Save with section",
    saveWithSectionHint: "Reopening this issue restores the cut and camera you can see now.",
    viewpointSaveFailed: "The issue was created, but the saved view could not be stored.",
    protocol: {
      button: "Create protocol",
      title: "Create protocol",
      description: "Choose the period, pick the issues, then choose what the document prints.",
      step: "Step {current} of {total}",
      datesStep: "Protocol period",
      today: "Today",
      thisWeek: "This week",
      thisMonth: "This month",
      customRange: "Custom range",
      customRangeHint: "Select a start date and an end date.",
      selectedRange: "Selected period: {range}",
      issuesStep: "Issues",
      issuesStepHint: "Untick anything that should not appear in the protocol.",
      selectAll: "Select all ({count})",
      selectedCount: "{count} of {total} selected",
      issuesLoading: "Loading issues…",
      issuesError: "The issues for this period could not be loaded.",
      retry: "Try again",
      noIssuesTitle: "No issues in this period",
      noIssuesDescription:
        "Nothing changed between these dates. Go back to choose another period, or continue to create an empty protocol.",
      includeIssueAria: "Include issue #{number}: {title}",
      openIssueAria: "Open issue #{number}: {title}",
      issueDetailTitle: "Issue details",
      pdfLanguage: "PDF language",
      pdfLanguageEt: "Estonian",
      pdfLanguageEn: "English",
      save: {
        created: "Your protocol is ready",
        createdDetail: "{filename} has been downloaded. Save it to a project as well?",
        saveToProject: "Save to project",
        uploading: "Saving to the project…",
        saved: "Protocol saved",
        savedInProject: "Filed in {project}.",
        savedInFolder: "Filed in {project} · {folder}.",
        unconfirmed: "Protocol save could not be confirmed",
        unconfirmedDetail:
          "The protocol may already be in the project. Check its files before trying to save it again.",
        failed: "The protocol could not be saved to the project.",
        dismiss: "Dismiss",
        unavailable: "Sign in to an organisation to save this to a project.",
        destinationTitle: "Where should the protocol go?",
        destinationDescription:
          "Choose the project and folder to file this protocol in. It stays on your device either way.",
        destinationSubmit: "Save here",
      },
      contentStep: "Document contents",
      contentStepHint:
        "Tick what the protocol should print. The period, the building and any caveats always appear.",
      sections: {
        summary: {
          name: "Issue summary",
          help: "How many issues in the period are in progress, resolved and closed.",
        },
        issues: {
          name: "Issue table",
          help: "One row per issue: its status, owners, disciplines and due date.",
        },
        activity: {
          name: "Activity log",
          help: "The dated changes made to each issue during the period.",
        },
      },
      columnsHint: "Choose what each row carries.",
      columns: {
        status: "Status",
        assignees: "Assignees",
        disciplines: "Disciplines",
        startDate: "Start date",
        dueDate: "Due date",
        commentCount: "Comments",
      },
      columnsCommentsHelp: "Adds a count to the table and prints the comments themselves below it.",
      commentsLoading: "Loading comments…",
      commentsError: "The comments could not be loaded, so the export is on hold.",
      filters: "Filters",
      filterStatus: "Status",
      filterDiscipline: "Discipline",
      filterClear: "Clear filters",
      filteredCount: "{count} of {total} in this period",
      noMatches: "No issues match these filters.",
      sortAscending: "Sorted ascending",
      sortDescending: "Sorted descending",
      documentDate: {
        label: "Date",
        hint: "Printed in the top-right corner of the protocol. Clear it to print no date.",
        placeholder: "Pick a date",
        time: "Time",
        hours: "Hour",
        minutes: "Min",
        clear: "Clear",
      },
      notesHeading: "Your own sections",
      notesHint:
        "Printed after the summary and before the issue table, in the order you arrange them.",
      addNote: "Add section",
      noteHeadingLabel: "Section heading",
      noteHeadingPlaceholder: "Decisions taken",
      noteBodyLabel: "Section text",
      noteBodyPlaceholder: "What was agreed, who is doing it, and by when.",
      removeNote: "Remove section",
      noteIncomplete: "Give every section both a heading and some text, or remove it.",
      nothingSelected: "Tick at least one section, or write one of your own.",
      issueCountOne: "1 issue in this period",
      issueCountMany: "{count} issues in this period",
      issueCountAtLeast: "At least {count} issues in this period",
      issueCountLoading: "Counting…",
      preview: "Preview",
      previewTitle: "Protocol preview",
      previewing: "Preparing preview…",
      previewError: "The preview could not be created. Please try again.",
      continue: "Continue",
      back: "Back",
      export: "Export",
      exporting: "Creating PDF…",
      exportSuccess: "Protocol saved as {filename}",
      exportError: "The protocol could not be created. Please try again.",
      attachments: {
        heading: "Attachments",
        hint: "Printed in the appendix, each under its own name: images as pictures, PDFs page by page. Other files are named only.",
        attach: "Attach",
        remove: "Remove from protocol",
        issueOption: "Attachments",
        issueOptionHelp: "The files attached to each issue.",
        inTable: "In the table",
        inAppendix: "In the appendix",
        issuesFound: "Files on the selected issues: {count}",
        issuesLoading: "Loading the issues' attachments…",
        issuesError: "The issues' attachments could not be loaded, so the export is on hold.",
        issuesNone: "None of the selected issues has attachments.",
        preparing: "Preparing attachments…",
        notIncluded:
          "Attachments that could not be included: {count}. The protocol lists them as such.",
        embedOriginals: "Include the original files",
        embedOriginalsHelp:
          "Each file attached here travels inside the PDF at full quality, opened from its paperclip. Makes the PDF larger; up to 50 MB of files.",
        originalsLeftOut:
          "Original files not included in the PDF: {count}. They are still printed or listed by name.",
        lookupError: "The chosen documents could not be added. Please try again.",
      },
      design: {
        button: "Design",
        title: "Protocol design",
        description: "Changes how the protocol looks. The content stays the same.",
        layouts: {
          classic: {
            name: "Classic",
            description: "The Franklin letterhead, a shaded details panel and outlined table rows.",
          },
          modern: {
            name: "Modern",
            description: "A bold colour band across the top, summary cards and a striped table.",
          },
          compact: {
            name: "Compact",
            description: "Smaller type and minimal chrome, so long registers take fewer pages.",
          },
        },
      },
      document: {
        title: "Protocol",
        period: "Period",
        building: "Building",
        project: "Project",
        allProjects: "All projects",
        unknown: "Unknown",
        contents: "Contents",
        selection: "Selection",
        selectionPartial: "{selected} of {total} issues in this period",
        generated: "Generated",
        summary: "Issue summary",
        inProgress: "In progress",
        resolved: "Resolved",
        closed: "Closed",
        issue: "Issue",
        status: "Status",
        assignees: "Assignees",
        disciplines: "Disciplines",
        startDate: "Start date",
        dueDate: "Due date",
        issues: "Issues",
        notes: "Notes",
        activity: "Activity",
        noActivity: "No issue activity was recorded in this period.",
        commentCount: "Comments",
        comments: "Comments",
        noComments: "No comments were recorded in this period.",
        commentEmpty: "(attachment only)",
        commentsTruncated:
          "This period holds more comments than one export can include. Only some of them are listed.",
        truncated:
          "This period holds more activity than one export can include. Only the most recent changes, and the issues they touched, are listed.",
        omitted:
          "Some changes in this period belong to issues that could not be read, and are not listed here.",
        page: "Page {current} of {total}",
        attachments: "Attachments",
        attachmentsAdded: "Added to this protocol",
        attachmentShownAbove: "Shown above",
        attachmentsWithoutPreview: "Files without a preview",
        attachmentUnsupported: "Format cannot be printed",
        attachmentFailed: "Could not be included",
        attachmentCaption: "Attachment {number} · {filename}",
      },
    },
    startAfterDue: "Start date is after the due date",
    loadingMore: "Loading the rest of the issues…",
    loadCeiling: "Showing the first {count} issues. Use search or filters to reach the rest.",
    listView: "List",
    kanbanView: "Kanban",
    ganttView: "Gantt",
    gantt: {
      day: "Day",
      week: "Week",
      month: "Month",
      resizeColumn: "Resize the title column",
      unscheduled: "Unscheduled",
      oneUnsavedChange: "1 unsaved change",
      unsavedChanges: "{count} unsaved changes",
      confirmChanges: "Confirm",
      discardChanges: "Discard",
      leaveTitle: "Leave without saving?",
      leaveBody:
        "The schedule changes you made here have not been saved. Leaving will discard them.",
      leaveStay: "Stay",
      leaveDiscard: "Discard changes",
      addIssueHere: "Add issue here",
      linkHint: "Drop on another issue · Esc to cancel",
      linkRefusedDuplicate: "These issues are already linked",
      linkRefusedCrossProject: "Only issues in the same project can be linked",
      linkRefusedCycle: "This link would make a loop",
      linkRefusedUnavailable: "This issue is no longer available",
      saveSnapshot: "Save snapshot",
      snapshotHistory: "Snapshot history",
      snapshotSaveTitle: "Save project snapshot",
      snapshotLabel: "Name",
      snapshotLabelPlaceholder: "Baseline programme",
      snapshotSaveAction: "Save snapshot",
      snapshotBlockedByChanges:
        "Confirm or discard your changes first — a snapshot records a saved schedule.",
      snapshotEmpty: "No snapshots saved yet.",
      snapshotRename: "Rename",
      snapshotRenameUnavailable: "Snapshots can't be renamed yet. Please try again later.",
      snapshotDeleteRefused:
        "This snapshot couldn't be deleted. It may already be gone, or you may not have permission.",
      snapshotViewing: "Viewing a snapshot",
      snapshotExit: "Back to the live schedule",
      snapshotCompare: "Compare with today",
      compareTitle: "Snapshot compared with today",
      compareSnapshotSide: "Snapshot · {label}",
      compareTodaySide: "Today",
      compareMoved: "Moved",
      compareAdded: "Added since",
      compareGone: "Gone since",
      compareUnchanged: "Unchanged",
      compareShowing: "Showing {shown} of {total}",
      compareShowUnchanged: "Show unchanged",
      compareNoRows: "Nothing to show with the current filter.",
      compareNothingChanged: "Nothing has moved since this snapshot.",
      compareTabTimeline: "Timeline",
      compareTabOverview: "Overview",
      compareColIssue: "Issue",
      compareColWas: "Was",
      compareColNow: "Now",
      compareColChange: "Change",
      compareBaselineBar: "Where this sat in the snapshot",
      compareLegendBaseline: "Baseline",
      compareLegendToday: "Today",
      compareCompletion: "Completion",
      compareDaysLater: "{count} days later",
      compareDaysEarlier: "{count} days earlier",
      compareDaysLonger: "{count} days longer",
      compareDaysShorter: "{count} days shorter",
      snapshotMeta: "{issues} issues · {deps} dependencies",
      snapshotMoved: "{count} moved",
      snapshotAdded: "added since",
      snapshotRemoved: "gone since",
      snapshotUnchanged: "Nothing has moved since this snapshot.",
      snapshotCompletionLater: "Completion is {days} day(s) later than this snapshot.",
      snapshotCompletionEarlier: "Completion is {days} day(s) earlier than this snapshot.",
      snapshotCompletionSame: "Completion is unchanged since this snapshot.",
      emptyTimeline: "No issues here yet. Add one to start the schedule.",
      truncated:
        "Showing {shown} of {total} issues — narrow the list with filters to see the rest.",
    },
    tabs: {
      all: "All",
      inProgress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
      overdue: "Overdue",
    },
    filters: {
      discipline: "Discipline",
      assignee: "Assignee",
      dueDate: "Due Date",
      project: "Project",
    },
    filterBy: {
      // "Filters" (plural) — same label the Documents toolbar uses.
      title: "Filters",
      status: "Status",
      overdue: "Overdue",
      overdueOnly: "Overdue only",
      overduePinsStatus: "Overdue issues are always in progress.",
      assignee: "Assignee",
      discipline: "Discipline",
      hasDocuments: "Has linked documents",
      noDocuments: "No linked documents",
      hasComments: "Has comments",
      noComments: "No comments",
      clearAll: "Clear all",
      moreFilters: "+{count} more",
      remove: "Remove",
      searchPlaceholder: "Search filters…",
      hiddenByFilters: "{count} issues hidden by filters",
      clearFilters: "Clear Filters",
    },
    contextMenu: {
      changeDescription: "Change description",
      status: "Status",
      assignees: "Assignees",
      discipline: "Discipline",
      startDate: "Start date",
      noStartDate: "No start date",
      dueDate: "Due date",
      noDueDate: "No due date",
      makeSubissue: "Mark as subissue",
      removeFromParent: "Remove from parent",
      createSubissue: "Create subissue…",
      addDocument: "Add document",
      moveToProject: "Move to project",
      noOtherProjects: "No projects available",
      askFranklin: "Ask Franklin",
      actions: "Actions",
      selectedCount: "{count} issues selected",
    },
    errors: {
      parentHasSubissues:
        "This issue already has subissues, so it can't become a subissue itself. Only one level of nesting is allowed.",
    },
    sortBy: {
      title: "Sort",
      creationDate: "Creation Date",
      discipline: "Discipline",
      status: "Status",
      assignee: "Assignee",
      dueDate: "Due Date",
    },
    dateBuckets: {
      today: "Today",
      yesterday: "Yesterday",
      thisWeek: "This week",
      thisMonth: "This month",
      older: "Older",
      noDate: "No date",
      overdue: "Overdue",
      later: "Later",
    },
    noDiscipline: "No discipline",
    noAssignee: "Unassigned",
    groups: {
      inProgress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    },
    detail: {
      status: "Status",
      assignee: "Assignee",
      assignees: "Assignees",
      discipline: "Discipline",
      startDate: "Start Date",
      setStartDate: "Set start date",
      dueDate: "Due Date",
      setDueDate: "Set due date",
      tags: "Tags",
      addTag: "Add tag",
      editTitle: "Edit title",
      assign: "Assign",
      elements: "Elements",
      created: "Created",
      history: "Issue history",
      linkedDocuments: "Linked Documents",
      noLinkedDocuments: "No linked documents.",
      addDocument: "Add Document",
      addFromFranklin: "Add from Franklin",
      uploadFiles: "Upload",
      uploading: "Uploading…",
      processing: "Processing…",
      processingShort: "Processing",
      previewUnavailable: "Preview not available",
      removeDocument: "Remove document",
      showAllAttachments: "Show all attachments ({count})",
      showFewerAttachments: "Show fewer",
      documents: "Documents",
      attach: "Attach",
      comments: "Comments",
      noComments: "No comments yet.",
      loadingComments: "Loading comments…",
      addComment: "Add a comment…",
      send: "Send",
      commentFailed: "Couldn't post your comment. It's back in the box — please try again.",
      mention: "Mention someone",
      justNow: "Just now",
      reply: "Reply",
      removeQuote: "Remove quote",
      edit: "Edit",
      edited: "Edited",
      name: "Name",
      issue: "Issue",
      description: "Description",
      addSubissue: "Add subissue",
      markAsSubissue: "Mark as subissue",
      pickParent: "Choose parent issue",
      searchIssues: "Search issues…",
      subissueOf: "Subissue of #{n} — {title}",
      documentThreadsTitle: "Comments for {filename}",
      removeFromParent: "Remove from parent",
      makeTopLevel: "Make this a top-level issue",
      linkedToIssue: "Attached to issue #{n} — {title}",
      linkedToIssues: "Attached to {count} issues",
      systemFolderLocked: "Files in Issue Attachments are added from the issues page.",
    },
    status: {
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    },
    checklist: {
      title: "Checklist",
      addChecklist: "Add checklist",
      removeChecklist: "Remove checklist",
      addToIssue: "Add to issue",
      addPlaceholder: "Add an item",
      deleteItem: "Delete item",
      reorderItem: "Drag to reorder",
      progress: "{done}/{total}",
      progressLabel: "Checklist: {done} of {total} done",
      tooLong: "A checklist item can be at most 500 characters.",
      stale: "The checklist changed while you were reordering it, so it has been reloaded.",
      loadFailed: "Couldn't load the checklist.",
    },
    dependencies: {
      title: "Dependencies",
      add: "Add",
      addDependency: "Add dependency",
      addWaitingOnHint:
        "Pick the issue that has to happen first. Both must be in the same project.",
      addDrivesHint: "Pick the issue that follows this one. Both must be in the same project.",
      waitingOn: "Waiting on",
      drives: "Drives",
      offPage: "Not loaded",
      linkedIssue: "the linked issue",
      abbrFS: "FS",
      abbrSS: "SS",
      abbrFF: "FF",
      abbrSF: "SF",
      pickRelationship: "Pick a relationship to save this dependency.",
      cancelDraft: "Discard",
      typeFS: "Finish → start",
      typeSS: "Start → start",
      typeFF: "Finish → finish",
      typeSF: "Start → finish",
      lag: "Lag in days",
      lagHint: "Days after the constraint. Negative overlaps the two.",
      rigid: "Rigid",
      flexible: "Flexible",
      rigidHint: "Rigid: this issue moves when its predecessor does.",
      flexibleHint:
        "Flexible: planned order only. Nothing moves on its own; you are warned instead.",
      pinnedHint: "These dates were set by hand, so they stay put when a predecessor moves.",
      unpin: "Follow predecessors",
      remove: "Remove dependency",
      empty:
        "Nothing scheduled around this one yet. A dependency makes one issue move when another does.",
      addWaitingOn: "This issue waits on…",
      addDrives: "This issue drives…",
      searchIssues: "Search issues…",
      noIssuesFound: "No issues found",
      relationship: "Relationship",
      movesTogether: "Moves together",
      days: "days",
      infoLabel: "About {label}",
      relationshipInfo: "Which date of the earlier issue controls which date of the later one.",
      typeFSInfo: "The later issue starts when the earlier one finishes. The usual choice.",
      typeSSInfo: "The later issue starts when the earlier one starts.",
      typeFFInfo: "The later issue finishes when the earlier one finishes.",
      typeSFInfo: "The later issue finishes when the earlier one starts. Rarely needed.",
      lagInfo: "Days between the two dates. +2 waits two days; −2 lets them overlap by two.",
      movesTogetherInfo: "What happens to the later issue when the earlier one moves.",
      rigidInfo: "Its dates shift automatically by the same amount.",
      flexibleInfo: "Nothing moves by itself; you get a warning if the order breaks.",
      waitingOnInfo: "Issues that have to happen before this one.",
      drivesInfo: "Issues that move when this one moves.",
      summaryFS: "{later} starts when {earlier} finishes",
      summarySS: "{later} starts when {earlier} starts",
      summaryFF: "{later} finishes when {earlier} finishes",
      summarySF: "{later} finishes when {earlier} starts",
      thisIssue: "this issue",
      summaryRigid: "moves together",
      summaryFlexible: "does not move on its own",
      unscheduled: "Unscheduled",
      violated: "Starts too early",
      violatedDetail: "This edge allows no earlier than {date}.",
      staged: "Unsaved",
      waitingCount: "{count} waiting on",
      drivesCount: "{count} driven",
      none: "None",
      lagLater: { one: "{count} day later", other: "{count} days later" },
      lagEarlier: { one: "{count} day earlier", other: "{count} days earlier" },
      lagNone: "no lag",
    },
    schedule: {
      affectsOne: "This change affects 1 dependent activity.",
      affectsMany: "This change affects {count} dependent activities.",
      batchAffectsOne: "Your changes move 1 other activity.",
      batchAffectsMany: "Your changes move {count} other activities.",
      nothingMoves: "Nothing else needs to move.",
      completionLater: "It moves project completion {days} day(s) later.",
      completionEarlier: "It brings project completion forward by {days} day(s).",
      completionUnchanged: "Project completion is unchanged.",
      movingLabel: "Moving",
      notMovingLabel: "Staying put",
      pinnedWarning: "#{n} {title} is pinned, so it keeps the dates someone chose.",
      softWarning: "#{n} {title} is planned to start on {date}, and now starts earlier.",
      bulkSummary: "{moved} dependent issue(s) moved, {held} left in place.",
      updateSchedule: "Update schedule",
      keepCurrentDates: "Keep current dates",
      applied: "Schedule updated — {count} issues moved",
      appliedOne: "Schedule updated — 1 issue moved",
      undo: "Undo",
      undone: "Schedule put back",
      undoFailed: "Could not put the schedule back.",
    },
    statuses: {
      newStatus: "New status",
      newTitle: "New status",
      editTitle: "Edit status",
      description: "Statuses belong to one project. Everyone working on it will see this one.",
      nameLabel: "Name",
      namePlaceholder: "Blocked",
      colourLabel: "Colour",
      previewLabel: "Preview",
      createAction: "Create status",
      deleteTitle: "Delete this status?",
      deleteChecking: "Checking whether any issues still use it…",
      deleteInUse:
        "{count} issue(s) still use this status. Move them to another status first — deleting it would leave them unusable.",
      deleteConfirm: "No issues use it, so it can be removed. This cannot be undone.",
      notInThisProject: "That status belongs to a different project, so this issue cannot take it.",
      orderNotSaved:
        "The card moved, but the new order was not saved — this column holds issues from a project you cannot manage.",
      reorderColumn: "Move this column",
      needsProject: "Choose a single project to add a status — a status belongs to one project.",
    },
    empty: "No issues found",
    emptyOverdue: {
      title: "No overdue issues",
      subtitle: "In-progress issues past their due date will show up here.",
    },
    addIssue: "Add Issue",
    search: "Search issues…",
    allProjects: "All projects",
    assigneesPlaceholder: "Select assignees…",
    searchUsers: "Search users…",
    noUsersFound: "No users found.",
    myDisciplines: "My disciplines",
    myDisciplinesTooltip: "Your responsibilities in this project, set on the Contacts page",
    suggestedAssignees: "Responsible for these disciplines",
    otherAssignees: "Everyone else",
    draft: {
      newIssue: "New issue",
      marker: "New",
      untitled: "No description yet",
      saving: "Saving…",
      create: "Create",
      creating: "Creating…",
      createNeedsDescription: "Add a description to create the issue",
      noProject: "There is no project to file this issue in",
      saveFailed: "The issue could not be saved. It is still open, so you can try again.",
      saveFailedWaiting:
        "The issue could not be saved. It is kept on the Issues page so you can try again.",
      reopen: "Open",
      discardFailed:
        "The issue could not be discarded. It is kept on the Issues page so you can try again.",
      uploadInProgress: "Wait for the upload to finish first",
      needsDescriptionTitle: "Add a description to save this issue",
      needsDescriptionBody:
        "An issue is saved under its description. Add one to keep what you have entered, or discard the issue.",
      addDescription: "Add description",
      discard: "Discard issue",
    },
    bulkActions: {
      issueSelected: "issue selected",
      issuesSelected: "issues selected",
      status: "Status",
      discipline: "Discipline",
      assignees: "Assignees",
      dueDate: "Due Date",
      askFranklin: "Ask Franklin",
      delete: "Delete",
      apply: "Apply",
      confirmDeleteTitle: "Delete issues?",
      confirmDeleteDescription: "Are you sure you want to delete {count} issue(s)?",
    },
  },
  activity: {
    historyTitle: "Issue history",
    historyEmpty: "No history yet.",
    system: "System",
    created: "{actor} created this issue",
    deleted: "{actor} deleted this issue",
    restored: "{actor} restored this issue",
    titleChanged: "{actor} changed the title to “{new}”",
    statusChanged: "{actor} changed status from {old} to {new}",
    startDateSet: "{actor} set the start date to {new}",
    startDateChanged: "{actor} changed the start date from {old} to {new}",
    startDateCleared: "{actor} cleared the start date",
    dueDateSet: "{actor} set the due date to {new}",
    dueDateChanged: "{actor} changed the due date from {old} to {new}",
    dueDateCleared: "{actor} cleared the due date",
    parentSet: "{actor} set the parent to #{n}",
    parentRemoved: "{actor} removed the parent issue",
    assigneeAdded: "{actor} assigned {name}",
    assigneeRemoved: "{actor} unassigned {name}",
    disciplineAdded: "{actor} added discipline {name}",
    disciplineRemoved: "{actor} removed discipline {name}",
    documentAttached: "{actor} attached {file}",
    documentDetached: "{actor} removed {file}",
    elementLinked: "{actor} linked element {element}",
    viewpointSaved: "{actor} saved a 3D view",
    viewpointCleared: "{actor} removed the saved 3D view",
    dependencyAdded: "{actor} made this depend on #{n}",
    dependencyRemoved: "{actor} removed the dependency on #{n}",
    schedulePinned: "{actor} pinned these dates",
    scheduleUnpinned: "{actor} let these dates follow their predecessors",
    drawingAnchorSaved: "{actor} saved a drawing location",
    drawingAnchorCleared: "{actor} removed the drawing location",
    elementUnlinked: "{actor} unlinked element {element}",
    commentAdded: "{actor} commented",
    checklistItemAdded: "{actor} added checklist item “{item}”",
    checklistItemRemoved: "{actor} removed checklist item “{item}”",
    checklistItemRenamed: "{actor} renamed checklist item “{old}” to “{new}”",
    checklistItemChecked: "{actor} ticked off “{item}”",
    checklistItemUnchecked: "{actor} unticked “{item}”",
    commentEdited: "{actor} edited a comment",
  },
  notifications: {
    title: "Notifications",
    subtitle: "Everything that's happened on issues you can access",
    empty: {
      title: "Nothing to show yet",
      subtitle: "Changes to issues you can access will appear here.",
    },
    filter: {
      // Plural, matching the Documents and Issues toolbars.
      title: "Filters",
      type: "Activity type",
      person: "Person",
      unread: "Unread",
      unreadOnly: "Unread only",
      timeRange: "Time range",
      clearAll: "Clear all",
      categories: {
        created: "Created / deleted",
        title: "Title",
        status: "Status",
        // Covers start AND due date changes — one row rather than two, since
        // most issues never set a start date.
        dueDate: "Dates",
        assignees: "Assignees",
        disciplines: "Disciplines",
        files: "Files",
        elements: "BIM elements",
        comments: "Comments",
        parent: "Parent",
        checklist: "Checklist",
        dependencies: "Dependencies",
      },
      ranges: {
        all: "All time",
        today: "Today",
        last7: "Last 7 days",
        last30: "Last 30 days",
        custom: "Custom range",
      },
      customRangeHint: "Pick a start and end day — the same day twice for one day",
      apply: "Apply",
    },
  },
  common: {
    filter: "Filter",
    search: "Search",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    confirmDelete: "Are you sure you want to delete this?",
  },
};
